/* eslint-disable no-nested-ternary */
import { useEffect, useState } from 'react';

import {
  Add,
  Button,
  ChakraBox,
  ChakraFlex,
  ChakraHStack,
  ChakraTab,
  ChakraTabList,
  ChakraTabPanel,
  ChakraTabPanels,
  ChakraTabs,
  ChakraText,
  Close,
  EquipUIFireflyV2Theme,
  EquipUITheme,
  IconButton,
  MenuSelectOption,
} from '@equip.health/ui';
import { filter } from 'lodash';

import { pageCount as pageCountOptions } from '~/lib/constants/list.constants';
import Loading from '~/pages/loading';
import { useGetPatientClinicalMeasurementsQuery } from '~/store/services/clinicalData';

import KeyVitalsList from './KeyVitalsList';
import {
  PatientVitalsTabsCollection,
  PatientVitalsTabsDefinition,
  VITALS_TABS_HEIGHT,
  VITALS_TABS_PADDING,
} from '../constants';

const { label: labelStyle, labelBold } = EquipUITheme.typography;
const { neutral } = EquipUIFireflyV2Theme.colors;

const TOP_LEVEL_ID = 'patient-vitals';

const filteredTabs = filter(PatientVitalsTabsCollection, (d) => d.isVisible);
type KeyVitalsProps = {
  closeModal: () => void;
  initialTabIndex?: number;
  patientId: string;
};

const KeyVitals = ({
  closeModal,
  initialTabIndex = 0,
  patientId,
}: KeyVitalsProps) => {
  const [tabIndex, setTabIndex] = useState<number>(initialTabIndex);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<MenuSelectOption>(
    pageCountOptions[0],
  );
  const [observationType, setObservationType] = useState(
    filteredTabs[tabIndex].filterBy,
  );
  const [isAddingValue, setIsAddingValue] = useState<boolean>(false);
  const {
    data: observationsData,
    isError: observationsError,
    isLoading: isObservationsDataLoading,
  } = useGetPatientClinicalMeasurementsQuery({
    patientExternalId: patientId,
    filterBy: {
      'observation-type': observationType,
    },
    pageNumber,
    pageSize: pageSize.value,
  });

  const onPageSizeChange = (updatedPageSize: MenuSelectOption) => {
    if (pageSize?.value !== updatedPageSize?.value) setPageNumber(1);
    setPageSize(updatedPageSize);
  };

  useEffect(() => {
    const type = filteredTabs[tabIndex].key;
    setObservationType(PatientVitalsTabsCollection[type].filterBy);
    setIsAddingValue(false);
    setPageNumber(1);
  }, [tabIndex]);

  return (
    <ChakraBox borderLeft={`1px solid ${neutral.line}`} flexGrow={1}>
      <ChakraTabs
        defaultIndex={0}
        index={tabIndex}
        isLazy
        onChange={(index) => {
          setTabIndex(index);
        }}
        variant="soft-rounded"
      >
        <ChakraFlex
          borderBottom={`1px solid ${neutral.line}`}
          justifyContent="space-between"
          padding={`${VITALS_TABS_PADDING}px`}
        >
          <ChakraTabList
            background="neutral.background.secondary"
            borderRadius="29px"
            height={`${VITALS_TABS_HEIGHT}px`}
            padding="6px"
            width="fit-content"
          >
            {Object.entries(PatientVitalsTabsCollection)
              .filter((tab) => tab[1].isVisible)
              .map(
                ([_tab, { label, key }]: [
                  string,
                  PatientVitalsTabsDefinition,
                ]) => {
                  return (
                    <ChakraTab
                      _selected={{
                        backgroundColor: 'primary.100',
                        boxShadow: 'none',
                        color: 'white',
                      }}
                      color="neutral.secondary"
                      key={key}
                      lineHeight="16.8px"
                      padding="6.5px 12px"
                    >
                      <ChakraFlex justifyContent="space-between">
                        <ChakraText {...labelStyle}>{label}</ChakraText>
                      </ChakraFlex>
                    </ChakraTab>
                  );
                },
              )}
          </ChakraTabList>
          <ChakraHStack spacing="10px">
            <Button
              {...labelBold}
              id={`${TOP_LEVEL_ID}__add-value`}
              onClick={() => setIsAddingValue(true)}
              rightIcon={
                <Add
                  fill={neutral.background.primary}
                  height="16px"
                  width="16px"
                />
              }
              size="md"
            >
              Add value
            </Button>
            <IconButton
              ariaLabel="close"
              icon={
                <Close fill={neutral.secondary} height="24px" width="24px" />
              }
              id={`${TOP_LEVEL_ID}__close`}
              onClick={closeModal}
              variant="ghost"
            />
          </ChakraHStack>
        </ChakraFlex>
        <ChakraBox padding={`${VITALS_TABS_PADDING}px`}>
          <ChakraTabPanels height="100%">
            {Object.values(PatientVitalsTabsCollection)
              .filter((tab) => tab.isVisible)
              .map(({ key }: PatientVitalsTabsDefinition) => (
                <ChakraTabPanel
                  id={`${TOP_LEVEL_ID}__tab__${key}`}
                  key={`${TOP_LEVEL_ID}__tab__${key}`}
                  padding="0"
                >
                  {isObservationsDataLoading ? (
                    <Loading />
                  ) : observationsError ? (
                    <ChakraText color="critical.base" fontSize="12px">
                      {observationsError}
                    </ChakraText>
                  ) : (
                    <KeyVitalsList
                      data={observationsData}
                      finishAddingValue={() => setIsAddingValue(false)}
                      isAddingValue={isAddingValue}
                      onPageSizeChange={onPageSizeChange}
                      pageNumber={pageNumber}
                      pageSize={pageSize}
                      patientId={patientId}
                      setPageNumber={setPageNumber}
                      tab={key}
                    />
                  )}
                </ChakraTabPanel>
              ))}
          </ChakraTabPanels>
        </ChakraBox>
      </ChakraTabs>
    </ChakraBox>
  );
};

export default KeyVitals;
